import React, {useCallback, useMemo, useState} from 'react';

import {SearchAccount} from 'src/components/account-information/components/search-account';
import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {SearchTemplateUser} from 'src/components/search-template-user';
import {ReplaceConfirmMessage, ReplaceSearchesSettings} from 'src/modules/profile/user/components/replace-search-settings';

import Button from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';


import {TOOL_TITLE} from './constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {UsersAccountChangeRequest} from 'src/openapi/admin/models/UsersAccountChangeRequest';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Props = {
    userIds: number[],
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UsersAccountChangeRequest>,
};

const WORKSPACES_WARNING =
    'Please, pay attention that all users\' workspaces will be unavailable after changing the users\' account.';


export const UpdateUserAccountModal = ({userIds, onCloseModal, processAction}: Props) => {
    const [account, setAccount] = useState<AccountShortInfo | undefined>();
    const [selectedTemplateUser, setSelectedTemplateUser] = useState<UserShortInfo | undefined>();
    const [savedSearches, setSavedSearches] = useState<boolean>(false);
    const [emailAlerts, setEmailAlerts] = useState<boolean>(false);
    const [templates, setTemplates] = useState<boolean>(false);

    const updateAccount = useCallback(async (options?: ActionOptions) => {
        if (account) {
            return await processAction({
                replaceLayouts: templates,
                replaceSearches: savedSearches,
                replaceAlerts: emailAlerts,
                accountId: account.accountId,
                templateUserId: selectedTemplateUser?.userId || undefined,
                usersIds: userIds,
            }, options);
        }
    }, [account, selectedTemplateUser, userIds, emailAlerts, processAction, savedSearches, templates]);

    const showReplaceMessage = useMemo(() => {
        if (!selectedTemplateUser) {
            return true;
        }
        return !templates && !emailAlerts && !savedSearches;
    }, [savedSearches, emailAlerts, templates, selectedTemplateUser]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain>
                <Message color="warning">{WORKSPACES_WARNING}</Message>
                <Group direction="v" style={{padding: 24}}>
                    <SearchAccount
                        label="Select Account"
                        selectedAccount={account}
                        onSelect={setAccount}
                        width={400}
                    />
                    <SearchTemplateUser
                        accountId={account?.accountId}
                        label="Import alerts & layouts from user"
                        selectedTemplateUser={selectedTemplateUser}
                        onSelect={setSelectedTemplateUser}
                        disabled={!account}
                        width={400}
                        placeholder="Keep existing"
                    />
                    <ReplaceSearchesSettings {...{
                        selectedTemplateUserId: selectedTemplateUser?.userId || null,
                        savedSearches,
                        setSavedSearches,
                        templates,
                        setTemplates,
                        emailAlerts,
                        setEmailAlerts,
                    }}
                    />
                    <ReplaceConfirmMessage {...{
                        disabled: showReplaceMessage,
                        templates,
                        emailAlerts,
                        savedSearches,
                        buttonName: 'Change',
                    }}
                    />
                </Group>
            </DialogMain>
            <BulkActionFooter action="Change" onConfirm={updateAccount} disabled={!account}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>Cancel</Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
